import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  ViewContainerRef,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { CompanySwitchStrategy } from '@core/http-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types
import { tellentOrganizationData } from '@core/tellent-data'; // FIXME
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { TellentDevFlag } from '@core/tellent-types';
import {
  ButtonComponent,
  DialogService,
  DialogWidth,
  HeadingComponent,
  IconComponent,
  RtPlatform,
} from '@core/user-interface-legacy';

import { CurrentUserProfileCompaniesItemComponent } from './current-user-companies-item/current-user-profile-companies-item.component';
import { AddCompanyDialogComponent } from '../../add-company-dialog/add-company-dialog.component';
import { CompanySettingsAnalyticsService } from '../../services/company-settings.analytics';

@Component({
  selector: 'tlc-current-user-profile-companies',
  templateUrl: './current-user-profile-companies.component.html',
  styleUrl: './current-user-profile-companies.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    HeadingComponent,
    TranslatePipe,
    CurrentUserProfileCompaniesItemComponent,
  ],
})
export class CurrentUserProfileCompaniesComponent {
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  public store = inject(Store);
  public dialogService = inject(DialogService);
  public i18n = inject(I18nService);
  public companySwitch = inject(CompanySwitchStrategy);
  public viewContainerRef = inject(ViewContainerRef);
  public analytics = inject(CompanySettingsAnalyticsService);
  public platform = inject(RtPlatform);

  public currentCompany = this.currentUserFacade.organizationMembershipSafe;
  public currentUser = this.currentUserFacade.currentUser;

  public currenMembershipOrgGuid = computed(() => this.currentCompany()?.org?.guid);

  public canAddNewCompany = computed(() => {
    const currentCompany = this.currentCompany();
    const currentUser = this.currentUser();

    if (this.usersWithoutEmails() && !currentUser?.hasWorkEmail) {
      return false;
    }

    return currentCompany?.org.canCreateOrg;
  });

  public members = this.currentUserFacade.members;

  protected usersWithoutEmails = injectDevFlagSignal(TellentDevFlag.UsersWithoutEmails);

  public onAddOrg() {
    this.dialogService.openCustom(
      AddCompanyDialogComponent,
      {
        width: DialogWidth.SmallX,
        viewContainerRef: this.viewContainerRef,
        providers: [tellentOrganizationData],
        mobileOptions: this.platform.IS_MOBILE
          ? {
              mobileDialog: true,
              mobileViewTitle: this.i18n.translate('tlc.org.add_dialog.mobile_heading'),
            }
          : undefined,
      },
      {},
    );
  }

  public async leaveCompany(orgGuid: string, isOwner: boolean) {
    const confirmed = await firstValueFrom(
      isOwner ? this.openOwnerLeaveConfirmationDialog() : this.openLeaveConfirmationDialog(),
    );
    if (confirmed) {
      this.currentUserFacade.leaveCompany(orgGuid);
      this.analytics.trackLeaveCompany();
    }
  }

  public signIn(orgGuid: string) {
    this.companySwitch.switchCompany(orgGuid);
    this.analytics.trackSignIn('current_user_profile');
  }

  private openLeaveConfirmationDialog() {
    return this.dialogService.error({
      title: this.i18n.translate('tlc.current_user.companies.leave_company_dialog.title'),
      message: this.i18n.translate('tlc.current_user.companies.leave_company_dialog.description'),
      agreeLabel: this.i18n.translate(
        'tlc.current_user.companies.leave_company_dialog.agree_label',
      ),
    });
  }

  private openOwnerLeaveConfirmationDialog() {
    return this.dialogService.warning({
      title: this.i18n.translate('tlc.current_user.companies.owner_leave_company_dialog.title'),
      message: this.i18n.translate(
        'tlc.current_user.companies.owner_leave_company_dialog.description',
      ),
      agreeLabel: '',
    });
  }
}
